import { useLocation, useNavigate } from "react-router-dom";
import { Button, Dropdown } from "@heroui/react";

import UserBadge from "../common/UserBadge";

import { VisibilityChip, ArchivedChip, TopicChip } from "../common/StatusChip";
import { useDocumentTitle } from "../../services/pageTitle";

// Γραμμή πλοήγησης. Ο τίτλος έρχεται από τη σελίδα όταν μόνο αυτή τον γνωρίζει,
// αλλιώς από το `routes.js`.
//
// Ο χειρισμός του μενού σε κινητό δεν γίνεται με χειροκίνητη προσθήκη κλάσεων στο
// `<html>` και στο κουμπί, όπως στο πρότυπο του 2019, όπου η μετάβαση hamburger σε X
// ζούσε ολόκληρη μέσα στο style.css. Η κατάσταση ανήκει στο κέλυφος και το κουμπί
// απλώς την εναλλάσσει.
//
// **Ο λογαριασμός ζει εδώ και όχι στο πλαϊνό μενού**, για τρεις λόγους. Η κεφαλίδα
// είναι ορατή και στις μικρές οθόνες, όπου το πλαϊνό μενού είναι κρυμμένο πίσω από
// hamburger — η αποσύνδεση απαιτούσε δύο πατήματα σε κινητό. Το προφίλ και η
// αποσύνδεση είναι **λογαριασμός**, όχι πλοήγηση περιεχομένου, και το πλαϊνό μενού
// είναι πλοήγηση. Και ελευθερώνει το κάτω μέρος του μενού για το φίλτρο
// αρχειοθετημένων συλλογών.
//
// Η **κατάσταση της συλλογής** ανεβαίνει στην μπάρα αντί να επαναλαμβάνεται μέσα στη
// σελίδα: ήταν δύο φορές στην οθόνη, μία στην μπάρα ως τίτλος και μία στο σώμα ως
// επικεφαλίδα με τα ίδια chips.

export default function Header({ routes, pageTitle, pageStatus, user, onLogout, onToggleMenu, isMenuOpen }) {
    const location = useLocation();
    const navigate = useNavigate();

    // Οι σελίδες που γνωρίζουν τον τίτλο τους τον ανακοινώνουν, γιατί μια
    // διεύθυνση με αναγνωριστικό δεν τον περιέχει: στο /collections/<id> ο
    // τίτλος της συλλογής είναι γνωστός μόνο αφού φορτωθεί.
    const match = routes.find((route) => route.path === location.pathname);
    const title = pageTitle || (match ? match.name : "Η σελίδα δεν βρέθηκε");

    // P3-11. **Εδώ και όχι σε κάθε σελίδα χωριστά**: ο τίτλος υπολογίζεται ήδη σε
    // αυτό το σημείο για τη μπάρα, και δεύτερος υπολογισμός θα ήταν δεύτερη
    // διατύπωση του ίδιου κανόνα — δύο τίτλοι για την ίδια σελίδα, που μπορούν να
    // αποκλίνουν. Καλύπτει κάθε διαδρομή μέσα στο κέλυφος· τις τέσσερις οθόνες
    // ταυτοποίησης, που ζουν έξω από αυτό, τις καλύπτει το `AuthLayout`.
    useDocumentTitle(title);


    return (
        <header className="sticky top-0 z-20 flex items-center gap-3 border-b border-border bg-background/80 px-4 py-3 backdrop-blur-md lg:px-6">
            <Button
                aria-label={isMenuOpen ? "Κλείσιμο μενού" : "Άνοιγμα μενού"}
                aria-expanded={isMenuOpen}
                variant="ghost"
                onPress={onToggleMenu}
                className="shrink-0 lg:hidden"
            >
                <i className={isMenuOpen ? "fa-solid fa-xmark" : "fa-solid fa-bars"} aria-hidden="true" />
            </Button>

            <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-3 gap-y-1">
                <h1 className="min-w-0 truncate text-lg font-semibold text-foreground">{title}</h1>

                {pageStatus && (
                    <div className="flex flex-wrap items-center gap-2">
                        {pageStatus.topic && <TopicChip>{pageStatus.topic}</TopicChip>}
                        <VisibilityChip visibility={pageStatus.visibility} />
                        {pageStatus.archived && <ArchivedChip />}
                    </div>
                )}
            </div>

            <Dropdown>
                <Dropdown.Trigger
                    aria-label="Μενού λογαριασμού"
                    className="shrink-0 rounded-full focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus"
                >
                    {/* **Το ίδιο συστατικό με το παράθυρο του πόρου**, όχι δεύτερη
                        υλοποίηση: εδώ ζούσε χειρόγραφο `Avatar` με δικό του υπολογισμό
                        αρχικού γράμματος και δικό του χειρισμό της εικόνας που δεν
                        φορτώνει — τα ίδια τρία πράγματα, γραμμένα δύο φορές.

                        Το `size="md"` δεν είναι επιλογή: είναι το μέγεθος που είχε ήδη
                        η κεφαλίδα (40×40, μετρημένο) και δεν επιτρέπεται να αλλάξει,
                        γιατί ορίζει το ύψος της μπάρας.

                        Το όνομα **αποκρύπτεται κάτω από τα 640 πίξελ**: εκεί η μπάρα
                        έχει ήδη τίτλο σελίδας και chips κατάστασης, και το όνομα του
                        συνδεδεμένου είναι η πληροφορία που λείπει λιγότερο. */}
                    <UserBadge user={user} size="md" textClassName="hidden text-left sm:flex" />
                </Dropdown.Trigger>

                <Dropdown.Popover placement="bottom end">
                    <Dropdown.Menu>
                        <Dropdown.Section title={user?.fullName || user?.username || ""}>
                            <Dropdown.Item id="profile" onAction={() => navigate("/profile")}>
                                <i className="fa-solid fa-user w-4 text-center" aria-hidden="true" />
                                Προφίλ
                            </Dropdown.Item>
                            <Dropdown.Item id="logout" onAction={onLogout}>
                                <i className="fa-solid fa-right-from-bracket w-4 text-center" aria-hidden="true" />
                                Αποσύνδεση
                            </Dropdown.Item>
                        </Dropdown.Section>
                    </Dropdown.Menu>
                </Dropdown.Popover>
            </Dropdown>
        </header>
    );
}
